import { useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router';
import PageBreadcrumb from '../../components/common/PageBreadCrumb';
import PageMeta from '../../components/common/PageMeta';
import Button from '../../components/ui/button/Button';
import { checkPlanExists, type PlanResponse } from '../../lib/api/plans';
import { useModal } from '../../hooks/useModal';
import { Modal } from '../../components/ui/modal';
import AbbreviationTooltip from '../../components/ui/AbbreviationTooltip';

type State = 'TX' | 'NM';
type FilingType = 'w3a' | 'w3' | 'c103' | 'sundry';

interface LocationState {
  api14?: string;
  state?: State;
}

const TX_FILINGS: { id: FilingType; label: string; description: string; abbr?: string }[] = [
  {
    id: 'w3a',
    label: 'W-3A Plugging Plan',
    description: 'Generate a Texas Railroad Commission W-3A plugging plan.',
    abbr: 'W-3A',
  },
  {
    id: 'w3',
    label: 'W-3 Post Plugging Report',
    description: 'File a post plugging report for a completed Texas well.',
    abbr: 'W-3',
  },
];

const NM_FILINGS: { id: FilingType; label: string; description: string; abbr?: string }[] = [
  {
    id: 'c103',
    label: 'C-103 Plugging Application',
    description: 'Submit a New Mexico OCD C-103 plugging application.',
    abbr: 'C-103',
  },
  {
    id: 'sundry',
    label: 'Sundry Post Plugging Report',
    description: 'File a New Mexico sundry post plugging report.',
  },
];

export default function CreateFiling() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const prefill = (location.state as LocationState) ?? {};
  const prefillApi14 = prefill.api14 ?? searchParams.get('api14') ?? '';

  const [step, setStep] = useState<1 | 2 | 3>(prefill.state ? 2 : 1);
  const [selectedState, setSelectedState] = useState<State | null>(prefill.state ?? null);
  const [selectedFiling, setSelectedFiling] = useState<FilingType | null>(null);
  const [apiNumber, setApiNumber] = useState(prefillApi14);
  const existingPlanModal = useModal();
  const [existingPlanData, setExistingPlanData] = useState<PlanResponse | null>(null);
  const [isChecking, setIsChecking] = useState(false);

  const filingOptions = selectedState === 'TX' ? TX_FILINGS : NM_FILINGS;

  function handleSelectState(s: State) {
    setSelectedState(s);
    setSelectedFiling(null);
    setStep(2);
  }

  function handleSelectFiling(f: FilingType) {
    setSelectedFiling(f);
    setStep(3);
  }

  function handleBack() {
    if (step === 3) {
      setStep(2);
    } else if (step === 2) {
      setSelectedState(null);
      setSelectedFiling(null);
      setStep(1);
    }
  }

  async function handleSubmit() {
    if (!selectedState || !selectedFiling || !apiNumber.trim()) return;

    const api14 = apiNumber.trim();
    const jurisdiction = selectedState;

    // For plan generation filings (w3a/c103), check if a plan already exists
    if (selectedFiling === 'w3a' || selectedFiling === 'c103') {
      setIsChecking(true);
      const existingPlan = await checkPlanExists(api14, 'combined');
      setIsChecking(false);

      if (existingPlan) {
        setExistingPlanData(existingPlan);
        existingPlanModal.openModal();
        return;
      }

      navigate('/plan-generation', { state: { api14, jurisdiction } });
    } else {
      navigate('/w3-wizard/new', { state: { api14, jurisdiction } });
    }
  }

  function handleProceedOverwrite() {
    existingPlanModal.closeModal();
    const api14 = apiNumber.trim();
    const jurisdiction = selectedState!;
    navigate('/plan-generation', { state: { api14, jurisdiction } });
  }

  return (
    <>
      <PageMeta
        title="Create Filing | RegulAgent"
        description="Start a new regulatory filing"
      />
      <PageBreadcrumb pageTitle="Create Filing" />

      <div className="max-w-2xl mx-auto">
        <div className="rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
          {/* Header */}
          <div className="border-b border-gray-200 px-6 py-4 dark:border-white/[0.05] flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
                New Regulatory Filing
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                Step {step} of 3
              </p>
            </div>
            {step > 1 && (
              <button
                onClick={handleBack}
                className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
              >
                &larr; Back
              </button>
            )}
          </div>

          <div className="p-6">
            {/* Step 1: Select State */}
            {step === 1 && (
              <div className="space-y-4">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Select the state for this filing:
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {(['TX', 'NM'] as State[]).map((s) => (
                    <button
                      key={s}
                      onClick={() => handleSelectState(s)}
                      className="flex flex-col items-start p-5 rounded-xl border-2 border-gray-200 dark:border-white/[0.08] bg-white dark:bg-white/[0.03] hover:border-brand-500 hover:bg-brand-50 dark:hover:border-brand-400 dark:hover:bg-brand-500/10 transition-all text-left"
                    >
                      <span className="text-2xl font-bold text-gray-800 dark:text-white/90">{s}</span>
                      <span className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        {s === 'TX' ? 'Texas' : 'New Mexico'}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 2: Select Filing Type */}
            {step === 2 && selectedState && (
              <div className="space-y-4">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Select the filing type for{' '}
                  <span className="font-semibold">{selectedState === 'TX' ? 'Texas' : 'New Mexico'}</span>:
                </p>
                <div className="grid grid-cols-1 gap-3">
                  {filingOptions.map((f) => (
                    <button
                      key={f.id}
                      onClick={() => handleSelectFiling(f.id)}
                      className="flex flex-col items-start p-5 rounded-xl border-2 border-gray-200 dark:border-white/[0.08] bg-white dark:bg-white/[0.03] hover:border-brand-500 hover:bg-brand-50 dark:hover:border-brand-400 dark:hover:bg-brand-500/10 transition-all text-left"
                    >
                      <span className="text-base font-semibold text-gray-800 dark:text-white/90">
                        {f.abbr ? (
                          <>
                            <AbbreviationTooltip term={f.abbr} />{' '}
                            {f.label.slice(f.abbr.length + 1)}
                          </>
                        ) : f.label}
                      </span>
                      <span className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        {f.description}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 3: Enter Well API Number */}
            {step === 3 && (
              <div className="space-y-5">
                <div>
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Summary
                  </p>
                  <div className="rounded-lg bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.05] px-4 py-3 text-sm text-gray-600 dark:text-gray-400 space-y-1">
                    <div>
                      State:{' '}
                      <span className="font-medium text-gray-800 dark:text-white/80">
                        {selectedState === 'TX' ? 'Texas (TX)' : 'New Mexico (NM)'}
                      </span>
                    </div>
                    <div>
                      Filing:{' '}
                      <span className="font-medium text-gray-800 dark:text-white/80">
                        {filingOptions.find((f) => f.id === selectedFiling)?.label}
                      </span>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    <AbbreviationTooltip term="API14" /> Number
                  </label>
                  <input
                    type="text"
                    value={apiNumber}
                    onChange={(e) => setApiNumber(e.target.value)}
                    placeholder={
                      selectedState === 'TX'
                        ? 'e.g. 42-389-12345-0000'
                        : 'e.g. 30-015-12345-0000'
                    }
                    className="w-full px-3 py-2.5 text-sm border border-gray-300 dark:border-white/[0.1] rounded-lg bg-white dark:bg-gray-800 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    autoFocus
                  />
                </div>

                <div className="flex gap-3 pt-1">
                  <Button variant="outline" onClick={handleBack} type="button">
                    Back
                  </Button>
                  <Button
                    onClick={handleSubmit}
                    disabled={!apiNumber.trim() || isChecking}
                    className="flex-1"
                  >
                    {isChecking ? 'Checking...' : 'Continue'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Existing Plan Detection Modal */}
      <Modal isOpen={existingPlanModal.isOpen} onClose={existingPlanModal.closeModal} className="max-w-[500px] p-6">
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-amber-50">
            <svg className="h-8 w-8 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h4 className="mb-2 text-xl font-semibold text-gray-800 dark:text-white">Existing Plan Found</h4>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
            A plugging plan already exists for API <span className="font-mono font-medium">{apiNumber}</span>.
          </p>
          {existingPlanData && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
              Total cement: {existingPlanData.payload?.materials_totals?.total_sacks || '\u2014'} sacks
              {' \u2022 '}
              {existingPlanData.payload?.steps?.length || 0} steps
            </p>
          )}
          <div className="flex flex-col gap-3">
            <button
              onClick={handleProceedOverwrite}
              className="w-full rounded-lg bg-brand-500 px-4 py-2.5 text-sm font-medium text-white hover:bg-brand-600 transition-colors"
            >
              Generate New Plan (Overwrite)
            </button>
            <button
              onClick={() => {
                existingPlanModal.closeModal();
                navigate(`/plan/${existingPlanData?.plan_id}`);
              }}
              className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800 transition-colors"
            >
              View Existing Plan
            </button>
            <button
              onClick={existingPlanModal.closeModal}
              className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}
